import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { useAsync, useSession, useToast } from '../lib/store';
import { BreederPanel } from '../components/breeding/BreederPanel';
import type {
  GroveFeedResult,
  NpcActivityEntry,
  NpcTrainer,
  Pokemon,
  PublicProfile,
  WarmResult,
} from '../lib/types';
import { Bar, Button, EmptyState, ErrorState, Panel, TypeChip } from '../components/ui';
import { EggArt } from '../components/EggArt';
import { TeamPanel } from '../components/TeamPanel';
import { RoleBadge } from '../components/roles/RoleBadge';
import { TeamEditor } from '../components/TeamEditor';
import { TrainerCard } from '../components/profile/TrainerCard';
import { ProfileHeaderPanels, TrainerExp } from '../components/profile/ProfileHeader';
import { formatDate, timeAgo, titleCase } from '../lib/format';
import { feedToast, warmToast } from '../lib/reciprocal';

const BADGE_ART: Record<string, string> = {
  egg: '◓', dex: '▤', sparkle: '✦', heart: '♥', crown: '♛', star: '★', type: '◈', evolve: '⟳', leaf: '🌱',
};

/**
 * The trainer card.
 *
 * Who someone is, what they have collected, the six Pokemon they chose to be
 * known by, and the doors through to their Groves and their shop. It shows no
 * Grove Pokemon at all: where a Pokemon *lives* is a question the Grove page
 * answers, and answering it twice in two places is what made the old profile a
 * worse copy of the Grove.
 */
export function TrainerProfilePage() {
  const { username = '' } = useParams();
  const navigate = useNavigate();
  const { setWallet, celebrate, celebrateBadges, refresh } = useSession();
  const { push } = useToast();

  const state = useAsync<PublicProfile>(
    () => api.get<PublicProfile>(`/community/profile/${username}`),
    [username],
  );

  const [busyId, setBusyId] = useState<string | null>(null);
  const [doneIds, setDoneIds] = useState<Set<string>>(new Set());
  /** Team Pokemon fed during this visit, on top of the server's cooldowns. */
  const [fedIds, setFedIds] = useState<Set<string>>(new Set());
  const [editingTeam, setEditingTeam] = useState(false);

  /**
   * Feeding straight from the profile, so a visitor does not have to detour
   * through the Grove to do the one thing the page is inviting them to do.
   *
   * Uses the same `/grove/feed` endpoint the Grove scene does — it already
   * decides own-Pokemon vs someone-else's from who owns it, so there is one set
   * of feeding rules rather than two that can drift apart.
   */
  async function feed(pokemon: Pokemon) {
    setBusyId(pokemon.id);
    try {
      const result = await api.post<GroveFeedResult>('/grove/feed', { pokemonId: pokemon.id });
      setFedIds((current) => new Set(current).add(pokemon.id));
      setWallet(result.wallet);
      celebrate(result.unlocked);
      celebrateBadges(result.badges ?? []);

      // Patch the card in place so its friendship bar and level move at once.
      state.setData((current) =>
        current
          ? {
              ...current,
              team: current.team.map((slot) =>
                slot.pokemon.id === pokemon.id ? { ...slot, pokemon: result.pokemon } : slot,
              ),
            }
          : current,
      );

      // Compact by design: at most three short toasts, never a modal. Feeding
      // is meant to happen dozens of times in a session, and the phrasing lives
      // in `lib/reciprocal.ts` so the Grove and the Community page say exactly
      // the same things about the same action.
      if (result.isOwn) {
        push('success', `${result.pokemon.displayName} enjoyed that. +${result.expGranted} EXP`, '♥');
        if (result.leveledTo) {
          push('reward', `${result.pokemon.displayName} reached Level ${result.leveledTo}!`, '★');
        }
      } else {
        const lines = feedToast({
          pokemonName: result.pokemon.displayName,
          expGranted: result.expGranted,
          coinsEarned: result.coinsEarned,
          friendBonus: result.friendBonus,
          guildBonus: result.guildBonus,
          diminished: result.diminished,
          team: result.team,
          reciprocal: result.reciprocal,
        });
        push('success', lines.primary, '♥');
        if (result.leveledTo) {
          push('reward', `${result.pokemon.displayName} reached Level ${result.leveledTo}!`, '★');
        }
        if (lines.team) push('info', lines.team, '✦');
        if (lines.levels) push('reward', lines.levels, '★');
        if (lines.note) push('info', lines.note);
      }
      void refresh();
    } catch (caught) {
      push('error', caught instanceof ApiError ? caught.message : 'Could not feed that Pokémon.');
      // A cooldown means it is genuinely unavailable — reflect that rather than
      // leaving a button that will only fail again.
      if (caught instanceof ApiError && caught.code.includes('COOLDOWN')) {
        setFedIds((current) => new Set(current).add(pokemon.id));
      }
    } finally {
      setBusyId(null);
    }
  }

  async function warm(eggId: string) {
    setBusyId(eggId);
    try {
      const result = await api.post<WarmResult>('/community/warm', { eggId });
      setDoneIds((current) => new Set(current).add(eggId));
      setWallet(result.wallet);
      celebrate(result.unlocked);
      celebrateBadges(result.badges ?? []);
      const lines = warmToast(result);
      push(
        'success',
        result.isReady ? 'You warmed it the rest of the way — it can hatch now!' : lines.primary,
        '◔',
      );
      if (lines.own) push('info', lines.own, '🔥');
      // Only the egg *becoming ready* interrupts. Everything else here is a
      // running total; this is a Pokemon waiting to be met.
      if (lines.ready) push('reward', lines.ready, '🥚');
      else if (lines.note) push('info', lines.note);

      state.setData((current) =>
        current
          ? {
              ...current,
              eggs: current.eggs.map((egg) =>
                egg.id === eggId
                  ? { ...egg, progress: Math.min(egg.requiredProgress, egg.progress + result.progressGranted) }
                  : egg,
              ),
            }
          : current,
      );
      void refresh();
    } catch (caught) {
      push('error', caught instanceof ApiError ? caught.message : 'Could not warm that egg.');
      if (caught instanceof ApiError && caught.code === 'COOLDOWN') {
        setDoneIds((current) => new Set(current).add(eggId));
      }
    } finally {
      setBusyId(null);
    }
  }

  async function toggleFollow() {
    if (!state.data) return;
    const follow = !state.data.isFollowing;
    try {
      await api.post('/community/follow', { userId: state.data.profile.userId, follow });
      state.setData((current) => (current ? { ...current, isFollowing: follow } : current));
      push('success', follow ? 'Following!' : 'Unfollowed.');
    } catch {
      push('error', 'Could not update.');
    }
  }

  async function visitNext() {
    try {
      // Passing who we are looking at stops the shuffle handing back the same
      // trainer twice in a row, which on a small server happens constantly.
      const result = await api.get<{ username: string | null }>('/community/random', {
        exclude: username,
      });
      if (result.username) navigate(`/trainer/${result.username}`);
      else push('info', 'No one new to visit right now.');
    } catch {
      push('error', 'Could not find anyone.');
    }
  }

  if (state.error) return <ErrorState message={state.error} onRetry={state.reload} />;
  if (!state.data) return <div className="pxwell skeleton" style={{ height: 420 }} />;

  const {
    profile, npc, isSelf, isFollowing, team, groves, eggs, achievements, activity,
    interactable, header, roles,
  } = state.data;
  const cooldownEggs = new Set([...interactable.cooldownEggIds, ...doneIds]);
  const cooldownPokemon = new Set([...interactable.cooldownPokemonIds, ...fedIds]);
  const firstGrove = groves[0];

  const customization = state.data.customization;

  return (
    <div
      className={`stack profile-bg profile-bg--${profile.background} profile-theme--${customization.theme}`}
    >
      {/* --- Profile header --------------------------------------------------
          Two columns: identity on the left, everything a trainer is proud of on
          the right. That right-hand area used to be blank while the badge
          cabinet took a full-width section of its own further down, which made
          profiles tall, empty at the top and dense where nobody had scrolled.
          The rule now is: can this fit in space that already exists? */}
      <Panel>
        <div className="phead">
        <div className="phead__identity">
        <TrainerCard
          profile={profile}
          customization={customization}
          featured={state.data.featured}
        >
          {/* One Trainer Identity block, in the brief's order:
              name (the card's own nameplate) → roles → title → guild → bio →
              level and EXP → joined. The old version scattered these as five
              independently floating boxes with the title buried in a muted line
              beside the @handle; grouping them is most of what "cleaner" means
              here, and it costs no height. */}
          <div className="tident">
            {roles && roles.length > 0 && (
              <div className="role-row">
                {roles.map((role) => (
                  <span
                    key={role.key}
                    className="role-chip"
                    style={{
                      ['--role-accent' as string]: role.accent,
                      ['--role-accent-dark' as string]: role.accentDark,
                    }}
                  >
                    <RoleBadge role={role} size={22} />
                    <span className="role-chip__name">{role.name}</span>
                  </span>
                ))}
              </div>
            )}

            {/* The title, given a line of its own. It is chosen from things the
                player earned, so it says more about them than the handle does —
                it should not be a footnote to it. */}
            <div className="tident__title">
              {npc ? `${npc.trainerClass} · ${titleCase(npc.region.toLowerCase())}` : profile.title}
            </div>
            <div className="tiny muted tident__handle">@{profile.username}</div>

            <div className="row row--wrap tident__tags">
              {/* Never conditional on anything but `npc`. A Living Trainer must
                  be identifiable as one on every screen they appear. */}
              {npc && <span className="chip chip--npc" title="Game-controlled trainer">🌿 NPC Trainer</span>}
              {profile.stats?.guild && (
                <Link className="chip" to={`/guilds/${profile.stats.guild.key}`}>
                  🏰 {profile.stats.guild.name}
                </Link>
              )}
            </div>

            {npc && <p className="tiny npc-quote">“{npc.quote}”</p>}
            {profile.bio && <p className="tiny tident__bio">{profile.bio}</p>}

            {npc && npc.badges.length > 0 && (
              <div className="row row--wrap" style={{ gap: 4 }}>
                {npc.badges.map((badge) => (
                  <span key={badge} className="chip chip--sm">◈ {badge}</span>
                ))}
              </div>
            )}
            {npc && npc.favoriteTypes.length > 0 && (
              <div className="row row--wrap tiny" style={{ gap: 4, alignItems: 'center' }}>
                <span className="muted">Specialises in</span>
                {npc.favoriteTypes.map((type) => (
                  <TypeChip key={type} type={type} />
                ))}
              </div>
            )}

            {/* Level and EXP together — they are one fact, and the old layout
                put the level in a chip at the top and its bar six elements
                lower. A Living Trainer earns no trainer EXP, so the bar would
                sit at zero forever and read as a broken player rather than as
                an NPC. */}
            {!npc && (
              <TrainerExp
                level={profile.level}
                exp={profile.exp}
                needed={profile.expForNextLevel}
                ratio={profile.expRatio}
              />
            )}

            <div className="tiny muted">
              {npc
                ? `Around since ${formatDate(profile.joinedAt)} · always somewhere nearby`
                : `Joined ${formatDate(profile.joinedAt)} · Last seen ${timeAgo(profile.lastSeenAt)}`}
            </div>
          </div>
        </TrainerCard>

        {/* The action buttons, grouped directly under the identity block rather
            than floating beside it. Compact rather than full-height: they are a
            way out of this page, not the point of it. */}
        <div className="phead__actions">
            <Link
              className="btn btn--sm btn--go btn--block"
              to={isSelf ? '/grove' : `/grove/${profile.username}`}
            >
              🌿 {isSelf ? 'My Groves' : 'Visit Grove'}
            </Link>
            <Link
              className="btn btn--sm btn--block"
              to={isSelf ? '/bazaar/mine' : `/bazaar/${profile.username}`}
            >
              ▤ {isSelf ? 'My Bazaar' : 'Visit Bazaar'}
            </Link>

            {isSelf ? (
              <>
                <Button size="sm" block variant="info" onClick={() => setEditingTeam(true)}>
                  ★ Manage Team
                </Button>
                {/* To the customiser, not to Settings. Everything that decides
                    how this page looks — avatar, featured badges, ribbons,
                    accolades, highlights, backdrop — is on that screen now;
                    Settings is where you change your password. */}
                <Link className="btn btn--sm btn--block" to="/customise">
                  ✎ Edit profile
                </Link>
              </>
            ) : (
              <>
                {/* The server refuses friendship and trades with a Living
                    Trainer, so those buttons must not be here to be pressed. */}
                {!npc && (
                  <Link className="btn btn--sm btn--block" to={`/friends?add=${profile.username}`}>
                    ♥ Friend Request
                  </Link>
                )}
                <Button size="sm" block variant={isFollowing ? 'default' : 'info'} onClick={() => void toggleFollow()}>
                  {isFollowing ? '✓ Following' : '+ Follow'}
                </Button>
                <Button size="sm" block disabled title="Private messages are coming soon">
                  ✉ Message
                </Button>
                {!npc && (
                  <Link className="btn btn--sm btn--block" to={`/trades?with=${profile.username}`}>
                    ⇄ Offer a trade
                  </Link>
                )}
                <Button size="sm" block onClick={() => void visitNext()}>
                  Visit someone else →
                </Button>
                {!npc && (
                  <Button
                    size="sm"
                    block
                    variant="ghost"
                    onClick={() =>
                      push(
                        'info',
                        'To report someone, use the ⚑ on one of their chat messages so a moderator can see what happened.',
                      )
                    }
                  >
                    ⚑ Report
                  </Button>
                )}
              </>
            )}
        </div>
        </div>

        {/* The right-hand side. This is the space that used to be blank.
            Living Trainers chase nothing — no badges, no ribbons, no
            statistics — so their header is the identity column alone rather
            than four empty blocks explaining what they have not done. */}
        <ProfileHeaderPanels
          header={header}
          username={profile.username}
          isSelf={isSelf}
          badgeLayout={customization.badgeLayout}
          hidden={Boolean(npc)}
        />
        </div>
      </Panel>

      {/* --- My Team --------------------------------------------------------
          The first thing under the header, where the Badge Cabinet used to sit.
          The cabinet said "how long has this trainer been here", which the
          featured badges in the header now answer in a fraction of the space;
          six Pokemon say who somebody *is*, and that deserves the first
          full-width section.

          It is also the page's one interactive surface: every card carries a
          Feed button, which is what makes a profile something you do rather
          than something you look at. */}
      <Panel
        title={isSelf ? '★ My Team' : `★ ${profile.trainerName}'s Team`}
        hint={
          isSelf
            ? 'Your six. They gain EXP when you help other trainers.'
            : 'Feed one — your own Team grows too.'
        }
        action={
          isSelf ? (
            <Button size="sm" variant="info" onClick={() => setEditingTeam(true)}>
              Edit
            </Button>
          ) : undefined
        }
      >
        {team.length === 0 && !isSelf ? (
          <EmptyState art="★" title="No Team yet">
            {profile.trainerName} hasn't chosen their six.
          </EmptyState>
        ) : (
          // The layout is a wrapper class rather than a prop on TeamPanel:
          // all three arrangements are the same six cards in a different grid,
          // so this is genuinely a stylesheet decision, and pushing it into the
          // component would put three layouts of branching into JSX.
          <div className={`team-layout team-layout--${customization.teamLayout}`}>
            <TeamPanel
              slots={team}
              isSelf={isSelf}
              onEdit={() => setEditingTeam(true)}
              onFeed={(pokemon) => void feed(pokemon)}
              fedIds={cooldownPokemon}
              busyId={busyId}
            />
          </div>
        )}
      </Panel>

      {npc && <NpcPanel npc={npc} trainerName={profile.trainerName} />}

      {/* --- Their Groves ----------------------------------------------------
          Straight after the Team. The old order put a statistics table and
          a "kept" strip between the six Pokemon and the door into where they
          live, which is two panels of reading between the two things a visitor
          actually came to look at. */}
      <Panel
        title="🌿 Groves"
        hint={`${groves.length} habitat${groves.length === 1 ? '' : 's'}`}
        action={
          firstGrove ? (
            <Link
              className="btn btn--sm btn--go"
              to={isSelf ? '/grove' : `/grove/${profile.username}`}
            >
              {isSelf ? 'Open' : 'Visit →'}
            </Link>
          ) : undefined
        }
      >
        {groves.length === 0 ? (
          <EmptyState art="🌿" title="No Groves yet" />
        ) : (
          <div className="grove-cards">
            {groves.map((grove) => (
              <Link
                key={grove.id}
                className={`pxframe grove-card grove-bg--${grove.backgroundKey}`}
                to={`${isSelf ? '/grove' : `/grove/${profile.username}`}?g=${grove.id}`}
              >
                {/* The backdrop classes only style these child layers — the
                    class alone paints nothing, so the card has to carry the
                    same three spans the real scene does. */}
                <span className="grove__sky" aria-hidden />
                <span className="grove__horizon" aria-hidden />
                <span className="grove__ground grove-ground--grass" aria-hidden />
                <span className="grove-card__label">
                  <span className="grove-card__name">{grove.name}</span>
                  <span className="grove-card__count mono tiny">
                    {grove.pokemonCount} Pokémon
                  </span>
                </span>
              </Link>
            ))}
          </div>
        )}
      </Panel>

      {/* --- Their eggs ------------------------------------------------------ */}
      {eggs.length > 0 && (
        <Panel title="◔ In the hatchery" hint={isSelf ? '' : 'Warm them to speed up hatching'}>
          <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))' }}>
            {eggs.map((egg) => {
              const onCooldown = cooldownEggs.has(egg.id);
              const ready = egg.progress >= egg.requiredProgress;
              return (
                <div key={egg.id} className="pxframe egg-slot">
                  <EggArt appearance={egg.appearance} state={ready ? 'ready' : 'idle'} label={egg.familyName} />
                  <div className="mono tiny">{egg.familyName}</div>
                  <div style={{ width: '100%' }}>
                    <Bar ratio={egg.progress / egg.requiredProgress} variant="egg" />
                    <div className="tiny muted center" style={{ marginTop: 4 }}>
                      {egg.progress} / {egg.requiredProgress}
                    </div>
                  </div>
                  {!isSelf && (
                    <Button
                      size="sm"
                      block
                      variant={onCooldown || ready ? 'default' : 'gold'}
                      disabled={ready || !interactable.canInteract || onCooldown || busyId === egg.id}
                      loading={busyId === egg.id}
                      onClick={() => void warm(egg.id)}
                    >
                      {ready ? 'Ready!' : onCooldown ? '✓ Warmed' : 'Warm it'}
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        </Panel>
      )}

      {/* --- Specialty, then recent activity, then the rest -------------------
          Three columns of the same weight, so none of them becomes another
          full-width section. What used to be a standalone statistics table and
          a standalone "kept" strip is now a header block and this row — the
          same information, in space that already existed. */}
      <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', alignItems: 'start' }}>
        <BreederPanel userId={profile.userId} />

        <PreservedStrip
          username={profile.username}
          companions={state.data.companions}
          museum={state.data.museum}
        />

        {achievements.length > 0 && (
          <Panel title="★ Recent milestones">
            <div className="row row--wrap">
              {achievements.map((achievement) => (
                <div
                  key={achievement.key}
                  className="pxwell"
                  style={{ padding: 'var(--gap-sm)', minWidth: 130, flex: '1 1 130px' }}
                  title={achievement.description}
                >
                  <div className="row" style={{ gap: 8 }}>
                    <span style={{ fontSize: '1.3rem', color: 'var(--gold-dark)' }} aria-hidden>
                      {BADGE_ART[achievement.badge] ?? '★'}
                    </span>
                    <div>
                      <div className="mono tiny">{achievement.name}</div>
                      {achievement.completedAt && (
                        <div className="tiny muted">{timeAgo(achievement.completedAt)}</div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Panel>
        )}

        <Panel title="♥ Recent visitors">
          {activity.length === 0 ? (
            <EmptyState art="♡" title="No visitors yet" />
          ) : (
            <div className="stack-sm">
              {activity.map((entry) => (
                <div key={entry.id} className="row row--between tiny">
                  <span>
                    <Link to={`/trainer/${entry.actorName}`}>{entry.actorName}</Link>
                    {entry.actorIsNpc && <span className="chip chip--npc chip--sm" style={{ marginLeft: 4 }}>🌿</span>}{' '}
                    {entry.type === 'WARM_EGG' ? 'warmed an egg' : `fed ${entry.pokemonName ?? 'a Pokémon'}`}
                  </span>
                  <span className="muted">{timeAgo(entry.createdAt)}</span>
                </div>
              ))}
            </div>
          )}
        </Panel>
      </div>

      {editingTeam && (
        <TeamEditor
          initial={team.map((slot) => slot.pokemon)}
          onClose={() => setEditingTeam(false)}
          onSaved={() => state.reload()}
        />
      )}
    </div>
  );
}


/**
 * The "this one isn't a person" panel.
 *
 * It says so in plain words rather than relying on the 🌿 chip alone. A Living
 * Trainer that a player mistakes for a real one is the failure mode the whole
 * system is designed around: they feed your Pokemon and warm your eggs exactly
 * like a real trainer does, so the only thing separating the two is how clearly
 * we say it.
 */
function NpcPanel({ npc, trainerName }: { npc: NpcTrainer; trainerName: string }) {
  const activity = useAsync<{ activity: NpcActivityEntry[] }>(
    () => api.get(`/npcs/${npc.key}/activity`),
    [npc.key],
  );

  return (
    <Panel title="🌿 Living Trainer">
      <p className="tiny">
        {trainerName} is part of the world of PokeGrove, not another player. They visit Groves, feed
        Pokemon and warm eggs just like anyone else — and everything they give you is worth exactly
        what a real trainer's help is worth. They never trade, never sell, and never appear on a
        leaderboard.
      </p>

      {activity.data && activity.data.activity.length > 0 && (
        <ul className="stack-sm" style={{ marginTop: 'var(--gap)', listStyle: 'none', padding: 0 }}>
          {activity.data.activity.slice(0, 8).map((entry) => (
            <li key={entry.id} className="row row--between tiny">
              <span>{entry.summary}</span>
              <span className="muted mono">{timeAgo(entry.createdAt)}</span>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

/**
 * The preserved strip: Companions, Museum wings, and a favourite ribbon.
 *
 * One panel rather than three. Each of these has its own page, and giving each
 * a full-width panel on the profile would push the things a visitor came for —
 * the showcase, the cabinet — off the first screen. What belongs here is the
 * *headline* and a way in.
 */
function PreservedStrip({
  username,
  companions,
  museum,
}: {
  username: string;
  companions: PublicProfile['companions'];
  museum: PublicProfile['museum'];
}) {
  // The favourite ribbon used to live here. It now sits in the header's
  // Featured Ribbons block, alongside five others and with the Pokemon wearing
  // it named — which is what it should always have said.
  if (companions.total === 0 && museum.wings.length === 0) return null;

  return (
    <Panel title="✦ Kept">
      <div className="stack-sm">
        {companions.total > 0 && (
          <div className="stack-sm">
            <div className="row row--between">
              <span className="tiny mono muted">
                HALL OF COMPANIONS · {companions.total}
              </span>
              <Link to={`/companions/${username}`} className="tiny">
                Visit the Hall →
              </Link>
            </div>
            <div className="row row--wrap">
              {companions.entries.map((companion) => (
                <Link
                  key={companion.pokemonId}
                  to={`/pokemon/${companion.pokemonId}`}
                  className="pxwell"
                  style={{ padding: 4, display: 'grid', placeItems: 'center' }}
                  title={
                    companion.certificateNo
                      ? `${companion.displayName} — ${companion.certificateNo}`
                      : companion.displayName
                  }
                >
                  <img src={companion.sprite} alt="" className="pixel" width={48} height={48} />
                </Link>
              ))}
            </div>
          </div>
        )}

        {museum.wings.length > 0 && (
          <div className="stack-sm">
            <div className="row row--between">
              <span className="tiny mono muted">MUSEUM · {museum.complete} wings complete</span>
              <Link to={`/museum/${username}`} className="tiny">
                Walk through →
              </Link>
            </div>
            <div className="row row--wrap">
              {museum.wings.map((wing) => (
                <span
                  key={wing.key}
                  className={`chip ${wing.complete ? 'chip--on' : ''}`}
                  title={`${wing.owned} pieces`}
                >
                  {wing.name}
                  {wing.complete && ' ✓'}
                </span>
              ))}
            </div>
          </div>
        )}

      </div>
    </Panel>
  );
}
